<template>
  <div class="color-picker" :class="{ 'is-open': isOpen }">
    <span v-if="label" class="color-picker__label">{{ label }}</span>
    <el-color-picker
      ref="colorPicker"
      :value="value"
      :disabled="disabled"
      :show-alpha="false"
      color-format="hex"
      :predefine="predefine"
      :size="size"
      @input="handleInput"
      @active-change="handleActiveChange"
    />
    <span v-if="showHex">{{ displayValue }}</span>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'

export default defineComponent({
  name: 'ColorPicker',
  props: {
    value: {
      type: String,
      default: '#FFFFFF'
    },
    label: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    predefine: {
      type: Array as PropType<string[]>,
      default: undefined
    },
    size: {
      type: String as PropType<'medium' | 'small' | 'mini' | 'large'>,
      default: undefined
    },
    showHex: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    displayValue(): string {
      return (this.value || '').toUpperCase()
    }
  },
  data() {
    return {
      isOpen: false,
      stopOpenWatch: null as null | (() => void)
    }
  },
  mounted() {
    this.setDropdownPlacement()
    this.bindOpenState()
    this.updateIconClass(this.isOpen)
  },
  beforeDestroy() {
    if (this.stopOpenWatch) {
      this.stopOpenWatch()
      this.stopOpenWatch = null
    }
  },
  methods: {
    handleInput(val: string): void {
      this.$emit('input', val)
    },
    handleActiveChange(val: string): void {
      this.$emit('active-change', val)
    },
    setDropdownPlacement(): void {
      const picker = this.$refs.colorPicker as {
        $refs?: {
          dropdown?: {
            placement?: string
            currentPlacement?: string
            updatePopper?: () => void
          }
        }
      }
      const dropdown = picker?.$refs?.dropdown
      if (!dropdown) {
        return
      }
      dropdown.currentPlacement = 'bottom-start'
      dropdown.updatePopper?.()
    },
    bindOpenState(): void {
      const picker = this.$refs.colorPicker as {
        showPicker?: boolean
        $el?: HTMLElement
      }
      if (!picker) {
        return
      }
      this.stopOpenWatch = this.$watch(
        () => picker.showPicker,
        value => {
          this.isOpen = Boolean(value)
          this.updateIconClass(this.isOpen)
        },
        { immediate: true }
      )
    },
    updateIconClass(isOpen: boolean): void {
      const picker = this.$refs.colorPicker as {
        $el?: HTMLElement
      }
      const icon = picker?.$el?.querySelector?.('.el-color-picker__icon') as HTMLElement | null
      if (!icon) {
        return
      }
      icon.classList.remove('el-icon-close')
      if (isOpen) {
        icon.classList.add('el-icon-close')
      }
    }
  }
})
</script>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

.color-picker {
  display: inline-flex;
  gap: $spacing-unit;
  align-items: center;

  &__label {
    font-size: $font-size;
    color: $text-color;
  }

  &__hex {
    font-size: $font-size;
    color: $text-color;
  }

  :deep(.el-color-picker__icon) {
    display: inline-block !important;
  }

  :deep(.el-color-picker__empty) {
    display: none !important;
  }

  &:not(.is-open) {
    :deep(.el-color-picker__icon) {
      display: none !important;
    }
  }
}
</style>

<style lang="scss">
.el-color-dropdown__btns {
  display: flex;
  align-items: center;

  .el-color-dropdown__value {
    float: none;
    flex: 1;
  }

  .el-color-dropdown__link-btn {
    margin-left: 8px;
    white-space: nowrap;
  }

  .el-color-dropdown__btn {
    margin-left: 8px;
    white-space: nowrap;
  }
}
</style>
